import classNames from "classnames";
import {
  AutopilotSessionRunningState,
  AutopilotSessionRunningStateStatus,
} from "../../../../__generated__/graphql";
import {
  BuildingOfficeIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import pluralize from "pluralize";

const AutopilotSessionCardStateDetails = ({
  runningState,
  error,
}: {
  runningState: AutopilotSessionRunningState | null;
  error?: string | null;
}) => {
  if (
    !runningState?.currentContacts.length &&
    runningState?.multisearchDetail?.currentCompanyName
  ) {
    return (
      <>
        <BuildingOfficeIcon
          className={classNames("w-6", "h-6", "text-gray-500")}
        />
        <div>
          <div
            className={classNames(
              "text-xs",
              "text-gray-500",
              "font-light",
              "line-clamp-1",
            )}
          >
            Scanning company:{" "}
            {runningState.multisearchDetail.currentCompanyName}
          </div>
        </div>
      </>
    );
  }

  if (runningState?.currentContacts.length) {
    const firstContact = runningState.currentContacts[0];
    const displayContacts = runningState.currentContacts.slice(0, 5);
    const remainingCount = Math.max(0, runningState.currentContacts.length - 5);

    return (
      <>
        <div className="flex items-center -space-x-2">
          {displayContacts.map((contact, index) => (
            <div key={contact.id} className="relative">
              {contact.profilePictureUrl ? (
                <img
                  alt={`${contact.firstName} ${contact.lastName}`}
                  src={contact.profilePictureUrl}
                  className={classNames(
                    "w-6",
                    "h-6",
                    "rounded-full",
                    "object-cover",
                    "border-2",
                    "border-white",
                    index === 0 ? "z-10" : `z-${10 - index}`,
                  )}
                />
              ) : (
                <div
                  className={classNames(
                    "w-6",
                    "h-6",
                    "rounded-full",
                    "bg-gray-200",
                    "border-2",
                    "border-white",
                    "flex",
                    "items-center",
                    "justify-center",
                    index === 0 ? "z-10" : `z-${10 - index}`,
                  )}
                >
                  <UserCircleIcon className="w-4 h-4 text-gray-500" />
                </div>
              )}
            </div>
          ))}
          {remainingCount > 0 && (
            <div
              className={classNames(
                "w-6",
                "h-6",
                "rounded-full",
                "bg-gray-300",
                "border-2",
                "border-white",
                "flex",
                "items-center",
                "justify-center",
                "text-xs",
                "font-medium",
                "text-gray-600",
                "z-0",
              )}
            >
              +{remainingCount}
            </div>
          )}
        </div>
        <div>
          <div className={classNames("text-sm", "line-clamp-1")}>
            {firstContact.firstName} {firstContact.lastName}
            {runningState.currentContacts.length > 1 && (
              <span className="text-gray-500 font-light">
                {" "}
                and {runningState.currentContacts.length - 1}{" "}
                {pluralize("other", runningState.currentContacts.length - 1)}
              </span>
            )}
          </div>
          <div
            className={classNames(
              "text-xs",
              "text-gray-500",
              "font-light",
              "line-clamp-1",
            )}
          >
            {firstContact.title || ""}
            {firstContact.companyName ? ` @ ${firstContact.companyName}` : ""}
          </div>
        </div>
      </>
    );
  }

  if (runningState?.status === AutopilotSessionRunningStateStatus.Loading) {
    return (
      <div className={classNames("text-xs", "text-gray-500", "font-light")}>
        Finding matches...
      </div>
    );
  }

  if (runningState?.status === AutopilotSessionRunningStateStatus.Paused) {
    return (
      <div className={classNames("text-xs", "text-gray-500", "font-light")}>
        {error ? (
          <div>
            <div className="font-medium text-red-600">Paused with error</div>
            <div className="line-clamp-2">{error}</div>
          </div>
        ) : (
          "Paused"
        )}
      </div>
    );
  }

  return null;
};

export default AutopilotSessionCardStateDetails;
